import React, { useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Box, Typography, Skeleton } from '@mui/material';
import { TeamsList } from './TeamsList';
import { TeamsListingSkeleton } from './TeamsListingSkeleton';
import { useAppDispatch, useAppSelector } from '@redux/hooks';
import { fetchTeams } from '@redux/teams/teams.thunk';
import { getTeamsData, getTeamsStatus } from '@redux/teams/teams.selectors';
import { ApiStatus } from '@constants';
import { useIsPhone } from '@hooks';
import { SearchField } from 'components/common/SearchField/SearchField';
import { belowAppBar, navRailBreakpoint } from '@theme/variables';
import {
  HEADING_PADDING_BOTTOM,
  HEADING_PADDING_TOP,
  HEADING_TOP,
} from '../constants/sticky';

export const TeamsListing: React.FC = () => {
  const dispatch = useAppDispatch();
  const account = useAppSelector(getTeamsData);
  const status = useAppSelector(getTeamsStatus);
  const selectedAccountId = useAppSelector((state) => state.accounts.selected);
  const arePermissionsLoading = useAppSelector(
    (state) => state.accounts.loading,
  );
  const isPhone = useIsPhone();
  const [searchParams, setSearchParams] = useSearchParams();
  const query = searchParams.get('q') ?? '';

  const setQuery = (next: string) =>
    setSearchParams(
      (current) => {
        const params = new URLSearchParams(current);
        next ? params.set('q', next) : params.delete('q');
        return params;
      },
      { replace: true },
    );

  useEffect(() => {
    if (arePermissionsLoading) return;
    dispatch(fetchTeams({ force: true }));
  }, [dispatch, selectedAccountId, arePermissionsLoading]);

  const isLoading =
    arePermissionsLoading ||
    status === ApiStatus.LOADING ||
    status === ApiStatus.IDLE;

  const teamsUnavailable = (
    <Box
      display="flex"
      flexDirection="column"
      justifyContent="center"
      alignItems="center"
      minHeight={520}
    >
      <img
        src="https://ae-cp-static-files.s3.eu-central-1.amazonaws.com/client-dashboard/team.png"
        alt="No teams"
        width="172"
        height="172"
      />
      <Typography variant="h2" align="center">
        AgileEngine teams are unavailable
      </Typography>
      <Typography variant="body2" color="text.secondary" align="center">
        Contact your assigned delivery manager to change that
      </Typography>
    </Box>
  );

  return (
    <Box>
      <Typography
        variant="h2"
        typography={{ xs: 'h3', tablet: 'h2' }}
        title={account?.name}
        sx={{
          p: 2,
          pb: HEADING_PADDING_BOTTOM,
          pt: HEADING_PADDING_TOP,
          position: { xs: 'static', tablet: 'sticky' },
          top: belowAppBar(HEADING_TOP),
          zIndex: 100,
          backgroundColor: {
            xs: 'background.backgroundPrimary',
            tablet: 'common.white',
          },
          whiteSpace: { xs: 'normal', [navRailBreakpoint]: 'nowrap' },
          overflow: { xs: 'visible', [navRailBreakpoint]: 'hidden' },
          textOverflow: 'ellipsis',
        }}
      >
        {isLoading ? <Skeleton width={270} /> : account?.name}
      </Typography>

      {status === ApiStatus.COMPLETE && Boolean(account?.projects?.length) && (
        <Box px={{ xs: 0, tablet: 2 }} pt={1} pb={1.5}>
          <SearchField
            value={query}
            placeholder="Search"
            fullWidth={isPhone}
            onChange={setQuery}
          />
        </Box>
      )}

      <Box
        sx={{
          overflowX: {
            xs: 'visible',
            tablet: 'auto',
            [navRailBreakpoint]: 'visible',
          },
          overscrollBehaviorX: 'contain',
        }}
      >
        {isLoading && <TeamsListingSkeleton />}
        {status === ApiStatus.FAILED && teamsUnavailable}
        {status === ApiStatus.COMPLETE && account && (
          <TeamsList
            projects={account.projects ?? []}
            query={query}
            accountDeliveryManager={account.deliveryManager}
          />
        )}
        {status === ApiStatus.COMPLETE && !account && teamsUnavailable}
      </Box>
    </Box>
  );
};
